import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, poundsFromPence } from "../lib/api";
import { useEvent } from "../lib/event";
import { useAttraction } from "../lib/attraction";
import { formatSlotTime } from "../lib/timeFormat";
import DashboardLayout from "../components/DashboardLayout";
import CompactCalendar from "../components/CompactCalendar";
import { OrderStatusBadge } from "../components/OrderStatusBadge";
import { SkeletonRows } from "../components/Skeleton";
import { Search, ChevronLeft, ChevronRight, X } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso.length <= 10 ? iso + "T12:00:00" : iso);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}
function ticketSummary(o) {
  const items = [...(o.line_items || []), ...(o.addon_items || [])];
  if (!items.length) return "—";
  return items.map((li) => `${li.quantity}× ${li.name}`).join(", ");
}

export default function OrdersListPage() {
  const { currentEventId, currentEvent } = useEvent();
  const { attraction } = useAttraction() || {};
  const [data, setData] = useState({ orders: [], total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [date, setDate] = useState("");
  const [page, setPage] = useState(1);
  const [calDates, setCalDates] = useState([]);

  const loadCal = async () => {
    const params = new URLSearchParams();
    if (currentEventId) params.set("event_id", currentEventId);
    try { const r = await api.get(`/orders/calendar-dates?${params}`); setCalDates(r.data.dates || []); } catch { /* noop */ }
  };
  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (status) params.set("status", status);
    if (date) params.set("date", date);
    if (currentEventId) params.set("event_id", currentEventId);
    params.set("page", page); params.set("limit", 25);
    try { const r = await api.get(`/orders?${params}`); setData(r.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { setPage(1); }, [q, status, date, currentEventId]);
  useEffect(() => { load(); /* eslint-disable-line */ }, [q, status, date, currentEventId, page]);
  useEffect(() => { loadCal(); /* eslint-disable-line */ }, [currentEventId]);

  const from = data.total === 0 ? 0 : (data.page - 1) * 25 + 1;
  const to = Math.min(data.page * 25, data.total);

  return (
    <DashboardLayout title="Orders" subtitle={`${data.total} total${currentEvent ? ` · ${currentEvent.name}` : ""}`}>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6">
        {/* Left — filters + table */}
        <div>
          <div className="st-card p-4 mb-4 flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-2.5 text-[#7a7a7a]" size={16} />
              <input className="st-input" style={{ paddingLeft: 40 }} placeholder="Search name, email, order #"
                     value={q} onChange={(e) => setQ(e.target.value)} data-testid="orders-search" />
            </div>
            <select className="st-input max-w-[190px]" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="orders-status">
              <option value="">All statuses</option>
              <option value="confirmed">Confirmed</option>
              <option value="partially_refunded">Partially refunded</option>
              <option value="refunded">Refunded</option>
              <option value="cancelled">Cancelled</option>
              <option value="pending">Pending</option>
            </select>
            {date && (
              <button className="st-btn st-btn-ghost text-xs" onClick={() => setDate("")} data-testid="clear-date">
                <X size={12} /> {fmtDate(date)}
              </button>
            )}
          </div>

          <div className="st-card overflow-x-auto hidden lg:block">
            <table className="w-full text-sm min-w-[780px]">
              <thead className="text-left text-[10px] uppercase tracking-wider text-[#7a7a7a] bg-[#0c0c0c] border-b border-[#1a1a1a]">
                <tr>
                  <th className="px-4 py-3">Order</th><th>Customer</th><th>Booked</th><th>Visit</th>
                  <th>Time</th><th>Tickets</th><th>Total</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody data-testid="orders-tbody">
                {loading ? (
                  <SkeletonRows cols={9} rows={6} />
                ) : data.orders.length === 0 ? (
                  <tr><td colSpan={9} className="py-10 text-center text-[#8a8a8a]">No orders match.</td></tr>
                ) : (
                  data.orders.map((o) => (
                    <tr key={o.id} className="border-t border-[#161616] hover:bg-[#0e0e0e]">
                      <td className="px-4 py-3 st-mono text-[#7a7a7a] whitespace-nowrap">{o.order_number}</td>
                      <td>{o.customer_first_name || o.customer_name} {o.customer_last_name || ""}
                        <div className="text-xs text-[#8a8a8a]">{o.customer_email}</div></td>
                      <td className="text-xs text-[#bbb] whitespace-nowrap">{fmtDate(o.created_at)}</td>
                      <td className="text-xs whitespace-nowrap">{fmtDate(o.event_date)}</td>
                      <td className="text-xs st-mono whitespace-nowrap">{o.time_slot_start ? formatSlotTime(o.time_slot_start, o.time_slot_end, attraction) : "—"}</td>
                      <td className="text-xs text-[#bbb] max-w-[180px] truncate" title={ticketSummary(o)}>{ticketSummary(o)}</td>
                      <td className="font-semibold whitespace-nowrap">{poundsFromPence(o.gross_amount)}</td>
                      <td><OrderStatusBadge status={o.status} /></td>
                      <td><Link className="st-btn st-btn-ghost text-xs" to={`/dashboard/orders/${o.id}`} data-testid={`order-link-${o.id}`}>Manage</Link></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile — stacked order cards */}
          <div className="lg:hidden space-y-3" data-testid="orders-mobile-list">
            {loading ? (
              <div className="st-card p-6 text-center text-[#7a7a7a]">Loading…</div>
            ) : data.orders.length === 0 ? (
              <div className="st-card p-8 text-center text-[#8a8a8a]">No orders match.</div>
            ) : (
              data.orders.map((o) => (
                <Link
                  key={o.id}
                  to={`/dashboard/orders/${o.id}`}
                  data-testid={`order-card-${o.id}`}
                  className="st-card p-4 block active:bg-[#0e0e0e] transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="st-mono text-[#dc2626] text-sm font-semibold">{o.order_number}</div>
                      <div className="text-sm truncate mt-0.5">{o.customer_first_name || o.customer_name} {o.customer_last_name || ""}</div>
                      <div className="text-xs text-[#8a8a8a] truncate">{o.customer_email}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-semibold">{poundsFromPence(o.gross_amount)}</div>
                      <div className="mt-1"><OrderStatusBadge status={o.status} /></div>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-[#161616] grid grid-cols-2 gap-2 text-xs">
                    <div><span className="text-[#7a7a7a]">Visit</span><div className="text-[#ddd]">{fmtDate(o.event_date)}{o.time_slot_start ? ` · ${formatSlotTime(o.time_slot_start, o.time_slot_end, attraction)}` : ""}</div></div>
                    <div><span className="text-[#7a7a7a]">Booked</span><div className="text-[#ddd]">{fmtDate(o.created_at)}</div></div>
                    <div className="col-span-2"><span className="text-[#7a7a7a]">Tickets</span><div className="text-[#ddd] truncate">{ticketSummary(o)}</div></div>
                  </div>
                </Link>
              ))
            )}
          </div>

          <div className="flex items-center justify-between mt-4 text-sm text-[#8a8a8a]">
            <span data-testid="orders-showing">{data.total === 0 ? "No orders" : `Showing ${from}–${to} of ${data.total}`}</span>
            <div className="flex items-center gap-2">
              <button className="st-btn st-btn-ghost text-xs" disabled={data.page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="orders-prev"><ChevronLeft size={14} /> Prev</button>
              <span className="text-xs">Page {data.page} / {data.pages}</span>
              <button className="st-btn st-btn-ghost text-xs" disabled={data.page >= data.pages} onClick={() => setPage((p) => p + 1)} data-testid="orders-next">Next <ChevronRight size={14} /></button>
            </div>
          </div>
        </div>

        {/* Right — calendar filter */}
        <div className="lg:sticky lg:top-6 lg:self-start">
          <div className="text-[10px] uppercase tracking-wider text-[#7a7a7a] mb-2 px-1">Filter by visit date</div>
          <CompactCalendar dates={calDates} mode="selection" selectedDate={date}
                           onSelect={(d) => setDate(d.date === date ? "" : d.date)} testid="orders-calendar" />
          {date && <button className="st-link text-xs mt-2" onClick={() => setDate("")} data-testid="cal-clear">Clear date filter</button>}
        </div>
      </div>
    </DashboardLayout>
  );
}
